// Modules
import React from "react";

// Styles
import "./MvSpinner.sass";

// Components
import ToolTip from "../ToolTip";
import MvIcon from "Generic/MvIcon";
import MvSpinnerMobile from "./MvSpinnerMobile";
import { Position } from "Generic/ToolTip/ToolTip";

type MvSpinnerState = {
  open: boolean,
  text: string,
  showSoftError: boolean;
  showHardError: boolean;
};
type Tip = {
  label:string;
  desc:string;
};
type Props ={
  name:string;
  value: number;
  max: number;
  min:number;
  disabled: boolean;
  hardError:string;
  placeholder:string;
  toolTip?: [Tip];
  toolTipPosition?: Position;
  required:boolean;
  errorStyle?: React.CSSProperties;
  onChange: (e: any) => void;
  errorMessage?:string;
  softError?:string;
  label: string;
  isMobile: boolean;
  tabIndex?:number;
  getText: (num: number) => string;
}
class MvSpinner extends React.Component<Props, MvSpinnerState> {
  constructor(props:Props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.upButton = this.upButton.bind(this);
    this.downButton = this.downButton.bind(this);
    this.handleText = this.handleText.bind(this);
    this.incrementUpOrDown = this.incrementUpOrDown.bind(this);
    this.clear = this.clear.bind(this);
    this.drawResult = this.drawResult.bind(this);
    this.state = {
      text: "",
      open: false,
      showSoftError: false,
      showHardError: false,
    };
  }
  onChange(e:any) {
    let val = e.target ? e.target.value : e;
    val = parseInt(val);

    let event = {
      target: {
        type: "spinner",
        name: this.props.name,
        value: val
      }
    };
    this.props.onChange(event);
  }

  /**
   * Given a min and max number, return an array of integers in that range,
   * inclusive.
   */
  rangeBetween(min: number, max: number): Array<number> {
    let arr = [];
    for(let i = min; i <= max; i++) {
      arr.push(i);
    }
    return arr;
  }

  upButton() {
    let currentValue = this.props.value;
    if (currentValue === this.props.max) {
      this.setState({ showSoftError: true, showHardError: true });
    }

    if (currentValue < this.props.max && !this.props.disabled) {
      currentValue++;
      this.drawResult();
      this.onChange(currentValue);
    }

  }

  downButton() {
    let currentValue = this.props.value;
    if (currentValue > this.props.min && !this.props.disabled) {
      currentValue--;
      this.drawResult();
      this.onChange(currentValue);
    }
  }

  handleText(e:any) {
    let value = e.target.value;

    if (!isNaN(value) && value > this.props.max) {
      this.setState({ showHardError: true });
      return;
    }

    if (!isNaN(value)) {
      value = value.replace(/^0+/, '');
      value = parseInt(value.trim());
      this.onChange(value);
    }
  }

  clear() {
    this.setState({
      open: true
    });
  }

  drawResult() {
    this.setState({
      open: false,
      showSoftError: false,
      showHardError: false,
    });
  }

  incrementUpOrDown(e:any) {
    //keycode 38 and 40
    let keycode = e.keyCode;
    if (keycode === 38) {
      this.upButton();
    } else if (keycode === 40) {
      this.downButton()
    }
  }

  render() {
    let err;
    let softErr = false;
    if (this.props.value > this.props.max || this.state.showHardError) {
      err = this.props.hardError || "Maxiumum value exceeded";
    } else if(this.state.showSoftError) {
        //according to BIM-410 we have to show exclamation sign in any case
        //so, keep softErr flag negative to comply with the request
      softErr = false;
      err = this.props.softError;
    }

    let content;
    if(this.props.isMobile) {
      let options = this.rangeBetween(this.props.min, this.props.max).map((value) => {
        return {
          value: value,
          description: this.props.getText(value)
        }
      });
      content = <MvSpinnerMobile
                  value={this.props.value}
                  disabled={this.props.disabled}
                  items={options}
                  onChange={this.onChange}
                  label={this.props.placeholder}
                />;
    } else {
      content = <input
                  id={this.props.name}
                  disabled={this.props.disabled}
                  readOnly={this.props.disabled}
                  onBlur={this.drawResult}
                  onFocus={this.clear}
                  autoComplete="off"
                  tabIndex={this.props.tabIndex}
                  value={
                    !this.state.open && this.props.value
                      ? this.props.getText(this.props.value)
                      : this.props.value || ""
                  }
                  onChange={this.handleText}
                  onKeyUp={this.incrementUpOrDown}
                  name={this.props.name}
                  placeholder={this.props.placeholder}
                  type="text"
                  className="spinner-input"
                  aria-required={this.props.required ? "true" : "false"}
                />
    }

    return (
      <div
        tabIndex={1}
        onBlur={this.drawResult}
        className={
          "mv-spinner " +
          (err ? "error" : "") +
          (this.props.disabled ? "disabled" : "")
        }
      >
        {(err && !softErr) && (
          <MvIcon
            icon="exclamation"
            color="red"
            style={{ position: "absolute", left: "-11px", top: "-15px" }}
            height="13px"
            width="13px"
          />
        )}
        <label className="visuallyhidden" htmlFor={this.props.name}>{this.props.label}</label>
        {content}
        <div className="spinner-controls">
          <button
            tabIndex={-1}
            aria-label="increment"
            className="spinner-control-up"
            type="button"
            onClick={this.upButton}
          />
          <button
            tabIndex={-1}
            aria-label="decrement"
            className="spinner-control-down"
            type="button"
            onClick={this.downButton}
          />
        </div>
        <div className="error tool-tip">
          <div
            className={"tool-tip-popup " + (err ? "show" : "")}
            style={this.props.errorStyle}
          >
            <p>{err}</p>
          </div>
        </div>
        {this.props.toolTip &&
          <div className="mv-spinner-tooltip">
            <ToolTip
              useRefToPosition={false}
              tabIndex={this.props.tabIndex}
              tip={this.props.toolTip}
              id='spinner-tooltip'
              position={this.props.toolTipPosition}
            >
              <span className="question-icon">{"?"}</span>
            </ToolTip>
          </div>
        }
        {this.props.required && <span>*</span>}
      </div>
    );
  }
}
export default MvSpinner;
